import { numberLocaleFor } from "../i18n/locale";
import type { Locale } from "../i18n/locale";
import { LINE_ITEM_COLORS } from "../lib/chartColors";
import {
  buildCostMixDonutSlices,
  COST_MIX_DONUT,
} from "../lib/costMixDonut";
import type { CostEstimate } from "../lib/types";
import type { UiKey } from "../i18n/ui";

type Props = {
  locale: Locale;
  t: (key: UiKey) => string;
  estimate: CostEstimate;
};

export function CostMixChart({ locale, t, estimate }: Props) {
  const total = estimate.totalMonthlyUsd;
  const nf = numberLocaleFor(locale);
  const pct = (n: number) => (total > 0 ? Math.max(0.35, (n / total) * 100) : 0);
  const donutSlices = buildCostMixDonutSlices(estimate);
  const { cx, cy, rOuter, rInner, vb } = COST_MIX_DONUT;

  const fmtUsd = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  return (
    <div>
      <h2>{t("mix.title")}</h2>
      <p className="mix-pie-hint">{t("mix.pieHint")}</p>
      <div className="cost-mix-layout">
        <div className="cost-mix-pie-wrap">
          <svg
            className="cost-mix-pie"
            viewBox={`0 0 ${vb} ${vb}`}
            role="img"
            aria-label={t("mix.pieAria")}
          >
            {donutSlices.length === 0 ? (
              <circle
                cx={cx}
                cy={cy}
                r={(rOuter + rInner) / 2}
                fill="none"
                stroke="color-mix(in srgb, var(--border) 70%, transparent)"
                strokeWidth={rOuter - rInner}
              />
            ) : (
              <g>
                {donutSlices.map((s) =>
                  s.kind === "ring" ? (
                    <g key={s.id}>
                      <title>
                        {s.row.label}: {fmtUsd(s.row.monthlyUsd)} (
                        {total > 0
                          ? `${((s.row.monthlyUsd / total) * 100).toLocaleString(nf, { maximumFractionDigits: 1 })}%`
                          : "—"}
                        )
                      </title>
                      <circle
                        cx={cx}
                        cy={cy}
                        r={rOuter}
                        fill={s.fill}
                        stroke="var(--surface)"
                        strokeWidth={0.9}
                      />
                      <circle
                        cx={cx}
                        cy={cy}
                        r={rInner}
                        className="cost-mix-donut-hole"
                      />
                    </g>
                  ) : (
                    <path key={s.id} d={s.path} fill={s.fill}>
                      <title>
                        {s.row.label}: {fmtUsd(s.row.monthlyUsd)} (
                        {total > 0
                          ? `${((s.row.monthlyUsd / total) * 100).toLocaleString(nf, { maximumFractionDigits: 1 })}%`
                          : "—"}
                        )
                      </title>
                    </path>
                  ),
                )}
              </g>
            )}
          </svg>
        </div>

        <div className="cost-mix-stack">
          <div
            className="cost-mix-bar"
            role="img"
            aria-label={t("mix.barAria")}
            title={estimate.lineItems.map((r) => `${r.label}: ${r.monthlyUsd}`).join(" · ")}
          >
            {estimate.lineItems.map((row) => (
              <div
                key={row.id}
                className="cost-mix-bar__slice"
                style={{
                  width: `${pct(row.monthlyUsd)}%`,
                  background: LINE_ITEM_COLORS[row.id] ?? "#666",
                }}
              />
            ))}
          </div>
          <ul className="legend">
            {estimate.lineItems.map((row) => (
              <li key={row.id}>
                <span
                  className="swatch"
                  style={{ background: LINE_ITEM_COLORS[row.id] ?? "#666" }}
                />
                <span className="lab">{row.label}</span>
                <span className="amt">
                  {total > 0
                    ? `${((row.monthlyUsd / total) * 100).toLocaleString(nf, { maximumFractionDigits: 0 })}%`
                    : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <style>{`
        h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .mix-pie-hint {
          margin: 0 0 0.85rem;
          font-size: 0.76rem;
          line-height: 1.4;
          color: var(--muted);
          max-width: 40rem;
        }
        .cost-mix-layout {
          display: grid;
          grid-template-columns: minmax(140px, 200px) minmax(0, 1fr);
          gap: 1.15rem 1.5rem;
          align-items: center;
        }
        @media (max-width: 640px) {
          .cost-mix-layout {
            grid-template-columns: 1fr;
            justify-items: center;
          }
          .cost-mix-stack {
            width: 100%;
          }
        }
        .cost-mix-pie-wrap {
          width: 100%;
          max-width: 200px;
        }
        .cost-mix-pie {
          width: 100%;
          height: auto;
          display: block;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.14));
        }
        html[data-theme="light"] .cost-mix-pie {
          filter: drop-shadow(0 1px 3px rgba(30, 40, 55, 0.07));
        }
        .cost-mix-donut-hole {
          fill: var(--surface);
        }
        .cost-mix-stack {
          min-width: 0;
        }
        .cost-mix-bar {
          display: flex;
          width: 100%;
          min-width: 0;
          height: 14px;
          border-radius: 6px;
          overflow: hidden;
          background: var(--surface2);
          border: 1px solid var(--border);
        }
        .cost-mix-bar__slice {
          flex-shrink: 0;
          min-width: 0;
          height: 100%;
        }
        .legend {
          list-style: none;
          margin: 0.75rem 0 0;
          padding: 0;
          display: grid;
          gap: 0.35rem;
          grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
          font-size: 0.78rem;
        }
        .legend li {
          display: flex;
          align-items: center;
          gap: 0.35rem;
        }
        .swatch {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          flex-shrink: 0;
          box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--text) 14%, transparent);
        }
        .lab {
          flex: 1;
          min-width: 0;
          color: var(--muted);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .amt {
          font-family: var(--mono);
          color: var(--text);
        }
      `}</style>
    </div>
  );
}
